'use client';
import * as React from 'react';
import { createEventBus } from '@/shared/utils';
import { Subscription } from '@/shared/i18n';
import { SubscriptionScreen, SubscriptionEvents, SubscriptionEventKeys } from '@/profile';
import { useAuthStore, useServerAuth } from '@/auth';
import { ProfileSubscriptionLoader } from 'src/modules/profile/ui/shared/module-loader';
import { useFirebaseAuth } from '@/shared/hooks';
import { openCustomerPortal } from 'src/modules/profile/subscription/services/open-customer-portal';
import { usePathname } from 'next/navigation';
import { isRight } from '@shared-commons/utils';
import { useRouter } from 'next/navigation';

interface SubscriptionContainerProps {
  dict: Subscription;
}

export function SubscriptionContainer({ dict }: SubscriptionContainerProps) {
  const subscriptionsEventBus = createEventBus<SubscriptionEvents>('subscription-scope');
  const { user } = useAuthStore();
  const auth = useFirebaseAuth();
  const { hydrated } = useServerAuth();
  const router = useRouter();

  const pathname = usePathname();

  const handleRedirect = React.useCallback((url: string) => router.push(url), [router]);

  React.useEffect(() => {
    async function handleOpenPortal() {
      const accessToken = await auth?.currentUser!.getIdToken(true);

      if (!accessToken || !user?.uid) return;
      const response = await openCustomerPortal(
        accessToken,
        user.uid,
        `${window.location.origin}${pathname}`,
      )();
      if (isRight(response)) {
        const data = await response.value;
        if (data) {
          window.location.href = data; // Reindirizza l'utente al Customer Portal
        }
      }
    }

    if (hydrated && !user?.email) {
      handleRedirect('/auth/login');
    }

    if (user?.email) {
      subscriptionsEventBus.emit(SubscriptionEventKeys.subscription.view, { email: user.email });
    }
    subscriptionsEventBus.on(SubscriptionEventKeys.payment.openPortal.init, handleOpenPortal);
    return () => {
      subscriptionsEventBus.off(SubscriptionEventKeys.payment.openPortal.init, handleOpenPortal);
    };
  }, [user?.email, hydrated, subscriptionsEventBus]);

  if (!user?.email || !auth) return <ProfileSubscriptionLoader key="profile-loader" />;

  return (
    <SubscriptionScreen
      auth={auth}
      eventBus={subscriptionsEventBus}
      dict={dict}
      key="subscription-screen"
    />
  );
}

export default SubscriptionContainer;
